import { memo, useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { Text, SearchBar, Spacer, Tabs, Icon, useTheme, Tooltip } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import { stripDiacritics } from 'utils/strings';

import AbsoluteEmpty from 'components/AbsoluteEmpty';
import AbsoluteError from 'components/AbsoluteError';
import AbsoluteLoader from 'components/AbsoluteLoader';
import { getCampaignReplaceVariables, getVariables } from 'services/MailingService';

const ENTITY_MAP = {
    companies: {
        label: 'label_account_fields',
        icon: 'accounts',
    },
    contacts: { label: 'label_contact_fields', icon: 'emptyAvatar' },
};

const defaultFormatInsertedVariable = (item, entityKey) => `{{ .${entityKey}.${item.id} }} `;

const VariablesPopover = ({
    onApply,
    useCampaignReplaceVariables = false,
    formatInsertedVariable,
}) => {
    const theme = useTheme();
    const [variables, setVariables] = useState();
    const [error, setError] = useState();
    const [loading, setLoading] = useState();
    const [tab, setTab] = useState();
    const debounce = useRef(null);
    const cachedData = useRef({});

    const getVariablesList = useCallback(() => {
        setLoading(true);
        const fetchList = useCampaignReplaceVariables ? getCampaignReplaceVariables : getVariables;
        fetchList()
            .then((data) => {
                cachedData.current = data || [];
                setVariables(data || []);
                const keys = data ? Object.keys(data) : [];
                if (keys.length > 1) {
                    setTab(keys[0]);
                }
            })
            .catch((e) => {
                console.error(e);
                setError(true);
            })
            .finally(() => setLoading(false));
    }, [useCampaignReplaceVariables]);

    const isSingleEntityMode = useMemo(() => {
        if (!variables) return false;
        const keys = Object.keys(variables);
        return keys.length === 1;
    }, [variables]);

    const singleEntityKey = useMemo(() => {
        if (!isSingleEntityMode || !variables) return null;
        return Object.keys(variables)[0];
    }, [isSingleEntityMode, variables]);

    const searchByText = useCallback(
        (text) => {
            clearTimeout(debounce.current);
            if (!text) {
                setVariables(cachedData.current);
                return;
            }

            const rawText = stripDiacritics(text?.toLowerCase()?.trim());
            debounce.current = setTimeout(() => {
                if (isSingleEntityMode && singleEntityKey && cachedData.current[singleEntityKey]) {
                    setVariables({
                        [singleEntityKey]: cachedData.current[singleEntityKey].filter((variable) =>
                            stripDiacritics(
                                (getLiteral(variable?.label) || '')?.toLowerCase()?.trim(),
                            ).includes(rawText),
                        ),
                    });
                } else {
                    setVariables(
                        Object.keys(cachedData.current).reduce((obj, entityKey) => {
                            if (!cachedData.current[entityKey]) return obj;
                            obj[entityKey] = cachedData.current[entityKey].filter((variable) =>
                                stripDiacritics(
                                    (getLiteral(variable?.label) || '')?.toLowerCase()?.trim(),
                                ).includes(rawText),
                            );
                            return obj;
                        }, {}),
                    );
                }
            }, 250);
        },
        [isSingleEntityMode, singleEntityKey],
    );

    const onChangeSearch = useCallback(
        (value) => {
            searchByText(value || '');
        },
        [searchByText],
    );

    const tabs = useMemo(() => {
        if (!variables || isSingleEntityMode) return null;
        return (
            <Tabs
                onChange={setTab}
                activeKey={tab}
                classes={{
                    navWrap: 'fm-ee-variables-nav-wrap',
                    tab: 'fm-ee-variables-tab',
                }}
                tabs={Object.keys(variables).map((entityKey) => {
                    const { label: tabLabelKey, icon: tabIcon } = ENTITY_MAP[entityKey];
                    return {
                        key: entityKey,
                        title: (
                            <Tooltip placement="top" content={getLiteral(tabLabelKey)}>
                                <span>
                                    <Icon
                                        className="fm-ee__variables-popover__tab-icon"
                                        name={tabIcon}
                                        color={
                                            tab === entityKey
                                                ? theme.colors.textLight.primary
                                                : theme.colors.grey[500]
                                        }
                                    />
                                </span>
                            </Tooltip>
                        ),
                    };
                })}
            />
        );
    }, [tab, theme.colors.grey, theme.colors.textLight.primary, variables, isSingleEntityMode]);

    const list = useMemo(() => {
        if (!variables) return null;

        const activeKey = isSingleEntityMode ? singleEntityKey : tab;
        const variablesToShow = activeKey ? variables?.[activeKey] : undefined;

        if (!variablesToShow?.length) {
            return (
                <AbsoluteEmpty
                    title={getLiteral('title_variables_not_found')}
                    subtitle={getLiteral('label_search_variables_not_found')}
                    size="popover"
                />
            );
        }

        const resolveInsertString = formatInsertedVariable || defaultFormatInsertedVariable;

        return (
            <div className="fm-ee__variables-popover__list">
                {variablesToShow.map((item) => (
                    <Text
                        key={item.id}
                        onClick={() => onApply(resolveInsertString(item, activeKey))}
                    >
                        {getLiteral(item.label)}
                    </Text>
                ))}
            </div>
        );
    }, [formatInsertedVariable, isSingleEntityMode, onApply, singleEntityKey, tab, variables]);

    useEffect(() => {
        getVariablesList();
    }, [getVariablesList]);

    useEffect(() => {
        return () => {
            clearTimeout(debounce.current);
        };
    }, []);

    const showEmptyView = !loading && !error && !variables;
    const showErrorView = !loading && error && !variables;

    return (
        <div className="fm-ee__variables-popover__inner">
            <div className="fm-ee__variables-popover__header">
                <Text type="subtitle1">{getLiteral('title_insert_fields')}</Text>
            </div>
            <div className="fm-ee__variables-popover__search-bar">
                <Spacer y={1} />
                <SearchBar
                    placeholder={getLiteral('placeholder_search_field')}
                    isFullWidth
                    useAsSimpleSearch
                    overrides={{
                        Select: {
                            customOnChangeInput: ({ inputValue }) => {
                                onChangeSearch(inputValue);
                            },
                        },
                    }}
                />
            </div>
            <div className="fm-ee__variables-popover__variables">
                {tabs}
                {list}
                {loading && <AbsoluteLoader label={getLiteral('label_loading_variables')} />}
                {showEmptyView && (
                    <AbsoluteEmpty
                        title={getLiteral('title_variables_not_found')}
                        subtitle={getLiteral('label_no_variables_found')}
                        size="popover"
                    />
                )}
                {showErrorView && (
                    <AbsoluteError
                        title={getLiteral('label_page_error_title')}
                        subtitle={getLiteral('label_no_variables_found')}
                        size="popover"
                    />
                )}
            </div>
        </div>
    );
};

export default memo(VariablesPopover);
